import React from 'react'
import 'dayjs/locale/en-gb'
import './datepicker-component.scss'
import { DemoItem as Container } from '@mui/x-date-pickers/internals/demo'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker as Picker } from '@mui/x-date-pickers/DatePicker'
import { DateTimePicker as MUIDateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import { convertDateToDayjs, concatClassName } from '../../utils/helpers'

interface DatePickerProps {
  label?: string
  value: Date | null
  required?: boolean
  minDate?: Date
  format?: string
  classes?: string[]
  dataTestId?: string
  changeHandler: (e: any) => void
}

interface DateTimePickerProps extends DatePickerProps {
  timeSteps?: {
    hours?: number
    minutes?: number
    seconds?: number
  }
}

const DatePicker = ({
  label,
  value,
  required,
  minDate,
  changeHandler,
  classes,
  dataTestId,
  format = 'DD/MM/YY',
}: DatePickerProps) => {
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale='en-gb'>
      <Container>
        <Picker
          label={label}
          data-testid={dataTestId}
          value={convertDateToDayjs(value)}
          {...(minDate !== undefined && minDate !== null
            ? { minDate: convertDateToDayjs(minDate) }
            : {})}
          format={format}
          onChange={(e) => changeHandler(e?.toDate())}
          className={concatClassName(classes)}
          sx={{
            '& .Mui-error': {
              '&.MuiInputBase-root .MuiOutlinedInput-notchedOutline': {
                borderColor: !value ? '#dcdcdc' : 'error.main', //setting to defaiult color when value is empty
              },
            },
          }}
          slotProps={{
            textField: {
              required,
            },
            desktopPaper: {
              sx: {
                maxHeight: 'min(640px, calc(100vh - 120px))',
                overflowY: 'auto',
                overflowX: 'hidden',
              },
            },
            popper: {
              sx: { zIndex: (t) => t.zIndex.modal + 1 },
            },
          }}
        />
      </Container>
    </LocalizationProvider>
  )
}

/** Above this threshold, MUI uses a single-column digital clock instead of two time columns (narrower popper). */
const TIME_SINGLE_COLUMN_THRESHOLD = 1500

const DateTimePicker = ({
  label,
  value,
  minDate,
  changeHandler,
  classes,
  dataTestId,
  format = 'DD/MM/YY',
  timeSteps = {
    minutes: 1,
  },
}: DateTimePickerProps) => {
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale='en-gb'>
      <Container>
        <MUIDateTimePicker
          label={label}
          data-testid={dataTestId}
          value={convertDateToDayjs(value)}
          {...(minDate !== undefined && minDate !== null
            ? { minDate: convertDateToDayjs(minDate) }
            : {})}
          format={format}
          onChange={(e) => changeHandler(e?.toDate())}
          className={concatClassName(classes)}
          timeSteps={timeSteps}
          thresholdToRenderTimeInASingleColumn={TIME_SINGLE_COLUMN_THRESHOLD}
          slotProps={{
            actionBar: { actions: ['accept'] },
            desktopPaper: {
              sx: {
                maxHeight: 'min(640px, calc(100vh - 120px))',
                overflowY: 'auto',
                overflowX: 'hidden',
              },
            },
            popper: {
              sx: { zIndex: (t) => t.zIndex.modal + 1 },
            },
          }}
        />
      </Container>
    </LocalizationProvider>
  )
}
export { DatePicker, DateTimePicker }
